import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react"
import { twMerge } from "tailwind-merge"

type TextareaProps = React.ComponentProps<"textarea"> & {
    minRows?: number
    maxRows?: number
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
    ({ className, minRows = 1, maxRows = 6, onChange, value, defaultValue, ...props }, ref) => {
        const innerRef = useRef<HTMLTextAreaElement | null>(null)

        useImperativeHandle(ref, () => innerRef.current as HTMLTextAreaElement)

        const resize = useCallback(() => {
            const el = innerRef.current
            if (!el) return

            el.style.height = "0px"

            const styles = window.getComputedStyle(el)
            const lineHeight = parseFloat(styles.lineHeight || "0") || 20
            const paddingTop = parseFloat(styles.paddingTop || "0")
            const paddingBottom = parseFloat(styles.paddingBottom || "0")
            const borderTop = parseFloat(styles.borderTopWidth || "0")
            const borderBottom = parseFloat(styles.borderBottomWidth || "0")

            const minH = minRows * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom
            const maxH = maxRows * lineHeight + paddingTop + paddingBottom + borderTop + borderBottom

            const next = Math.min(Math.max(el.scrollHeight, minH), maxH)
            el.style.height = `${next}px`
            el.style.overflowY = el.scrollHeight > maxH ? "auto" : "hidden"
        }, [minRows, maxRows])

        useEffect(() => {
            resize()
        }, [resize, value])

        const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            resize()
            onChange?.(e)
        }

        return (
            <textarea
                ref={innerRef}
                data-slot="textarea"
                rows={minRows}
                value={value}
                defaultValue={defaultValue}
                onChange={handleChange}
                className={twMerge(
                    `border-input placeholder:select-none placeholder:text-slate-800/50 flex w-full resize-none rounded-lg border bg-transparent px-3 py-2 text-base shadow-xs outline-none transition-[color,box-shadow] disabled:cursor-not-allowed disabled:opacity-50 whitespace-pre-wrap break-words
          `,
                    className
                )}
                {...props}
            />
        )
    }
)

Textarea.displayName = "Textarea"

export { Textarea }
